import type { StyleRule } from '@archipro-design/aria';
import { HeaderClassName } from '@archipro-design/aria';
import { pxArrayToRem } from '@archipro-design/aria';
import {
    pxToRem,
    labelClassName,
    boxClassName,
    buttonClassName,
} from '@archipro-design/aria';

import { ABOUT_BANNER_SIZE } from '@modules/professional/config/constants';

export const InfoContainer: StyleRule = ({ theme }) => ({
    display: 'block',
    justifyContent: 'normal',

    [theme.screen.laptop]: {
        gridTemplateColumns: `${pxToRem(ABOUT_BANNER_SIZE)} 1fr`,
        gridGap: pxToRem(43),

        display: 'inherit',
        justifyContent: 'inherit',
        marginBottom: 'inherit',
    },
});

export const InfoWrap: StyleRule = ({
    theme,
    hasBannerImage,
    hasCustomTabs,
}) => ({
    margin: pxArrayToRem([0, 18]),
    '& > div:not(:last-child)': {
        marginBottom: pxToRem(35),
    },

    [theme.screen.laptop]: {
        '& > div:not(:last-child)': {
            marginBottom: pxToRem(18),
        },
        [`& .${boxClassName} > *`]: {
            paddingBottom: 0,
        },

        margin: 'unset',
        borderTop: `1px solid ${theme.siteVariables.colors.primitive.black}`,

        borderBottom:
            hasBannerImage || hasCustomTabs
                ? 0
                : `1px solid ${theme.siteVariables.colors.primitive.black}`,
        minHeight: hasBannerImage ? 'unset' : pxToRem(346),
    },
});

export const AboutHeader: StyleRule = ({ theme }) => ({
    [`&.${HeaderClassName}`]: {
        letterSpacing: pxToRem(0.4),
        lineHeight: 1.75,
        fontSize: pxToRem(20),
        margin: pxArrayToRem([96, 0, 18]),
        ...theme.siteVariables.textStyles.Text.Medium65,
    },
});

export const ButtonStyle: StyleRule = ({ theme }) => ({
    [`& > button`]: {
        fontSize: theme.siteVariables.fontSizes.caption03,
        ':hover': {
            background: 'none',
        },
    },

    [`& > button .${labelClassName}`]: {
        fontSize: theme.siteVariables.fontSizes.caption03,
        lineHeight: 1,
        textUnderlineOffset: pxToRem(3),
    },

    [theme.screen.laptop]: {
        [`& > button .${labelClassName}`]: {
            fontSize: theme.siteVariables.fontSizes['label03-alt'],
            fontWeight: theme.siteVariables.fontWeightLight,
            letterSpacing: theme.siteVariables.letterSpacingSmall,
            lineHeight: theme.siteVariables.lineHeightSmall,
            marginTop: pxToRem(3),
        },
    },
});

export const ReadMoreText: StyleRule = ({ theme }) => {
    return {
        marginRight: 0,
        marginLeft: 0,

        '& > button': {
            padding: 0,
            marginTop: pxToRem(20),
            justifyContent: 'flex-start',
            display: 'flex',
            '& > div': {
                letterSpacing: theme.siteVariables.letterSpacingSmall,
                lineHeight: 1,
                textUnderlineOffset: pxToRem(3),
            },
        },
        [`> .${boxClassName} > *`]: {
            margin: 'unset',
        },

        [`& > .${boxClassName} p`]: {
            '& a': {
                color: 'inherit',
                textDecoration: `underline`,
                textDecorationThickness: pxToRem(1.5),

                '&:hover': {
                    color: `${theme.siteVariables.colors.charcoal['000']}`,
                },
            },
        },

        '& a': {
            color: 'inherit',
            textDecoration: `underline`,
            textDecorationThickness: pxToRem(1.5),

            '&:hover': {
                color: `${theme.siteVariables.colors.charcoal['000']}`,
            },
        },

        [`& > .${boxClassName} p:first-child`]: {
            marginTop: pxToRem(2),
        },

        [theme.screen.laptop]: {
            marginTop: 0,
            fontSize: theme.siteVariables.fontSizes['label03-alt'],
            fontWeight: theme.siteVariables.fontWeightLight,
            letterSpacing: theme.siteVariables.letterSpacingSmall,

            [`& > p:first-child`]: {
                marginTop: pxToRem(0),
            },
            [`& > p`]: {
                color: theme.siteVariables.colorScheme.default?.foreground,
                fontSize: theme.siteVariables.fontSizes['label03-alt'],
                fontWeight: theme.siteVariables.fontWeightLight,
                letterSpacing: theme.siteVariables.letterSpacingSmall,
                margin: '1em 0',
            },
        },

        [`& ul`]: {
            display: 'block',
            listStyle: 'revert',
            paddingLeft: 'revert',
            margin: 'revert',

            [`& *`]: {
                margin: '0 0 0.5rem 0',
            },

            [`& > li:last-child`]: {
                marginBottom: '1em',
            },
        },

        [`& ol`]: {
            display: 'block',
            listStyle: 'revert',
            paddingLeft: 'revert',
            margin: 'revert',

            [`& *`]: {
                margin: '0 0 0.5rem 0',
            },

            [`& > li:last-child`]: {
                marginBottom: '1em',
            },
        },
    };
};

export const hideToggleButton: StyleRule = {
    [`& .${buttonClassName}`]: {
        display: 'none',
    },
};

/** Container for enquiry, save, and visit website buttons on mobile. */
export const ButtonContainer: StyleRule = ({ theme }) => ({
    gap: theme.siteVariables.spaces['space-8'],
    marginTop: pxToRem(13),
    marginBottom: pxToRem(35),
});

/** Container for the enquiry button and save heart button. */
export const EnquireContainer: StyleRule = {
    position: 'relative',
};

/** Enquiry button style. */
export const EnquireButton: StyleRule = {
    width: '100%',
    height: pxToRem(48),
};

/** Heart button style for save to design board. */
export const ButtonHeart: StyleRule = ({ theme }) => ({
    cursor: 'pointer',
    width: pxToRem(48),
    height: pxToRem(48),
    backgroundColor: '#FFFFFF',
    color: '#222222',
    [`&.${buttonClassName}`]: {
        border: `${pxToRem(1)} solid ${
            theme.siteVariables.colors.charcoal['250']
        }`,
        [`&:hover`]: {
            backgroundColor: '#FFFFFF !important',
            border: `${pxToRem(1)} solid ${
                theme.siteVariables.colors.charcoal['250']
            }`,
        },
        [`&:active`]: {
            backgroundColor: '#FFFFFF !important',
            border: `${pxToRem(1)} solid ${
                theme.siteVariables.colors.charcoal['250']
            }`,
        },
    },
});

/** Container for the visit website button. */
export const SaveShareContainer: StyleRule = () => ({
    paddingTop: 0,
});

/** Visit website button style. */
export const ViewWebsiteButton: StyleRule = ({ theme }) => ({
    borderColor: theme.siteVariables.colors.charcoal['000'],
    width: '100%',
});
